/**
 * @description 全局请求拦截器 - 用于监听所有网络请求
 * @author Cursor
 * @date 2024-04-19
 */
import { CryptoService } from './crypto';

const TRACKING_ENDPOINT = '/berry/v1/rpt';

type TrackingXMLHttpRequest = XMLHttpRequest & {
  isTrackingRequest?: boolean;
};

// write by cursor
/**
 * 创建加密后的Blob数据
 * @param data 原始数据
 * @returns 加密后的Blob
 */
function createEncryptedBlob(data: unknown): Blob {
  const encryptedData = CryptoService.encrypt(data);
  return new Blob([encryptedData], { type: 'text/plain' });
}

function sendTrackingRequest(url: string, data: BodyInit | null, accessToken: string): void {
  void fetch(url, {
    method: 'POST',
    body: data,
    headers: { Authorization: `Bearer ${accessToken}` },
    keepalive: true,
  }).catch((error: unknown) => {
    console.error('send tracking request failed:', error);
  });
}

// write by cursor
/**
 * 初始化SendBeacon拦截器
 */
function initSendBeaconInterceptor() {
  const originalSendBeacon = navigator.sendBeacon;
  navigator.sendBeacon = function(url: string, data?: BodyInit | null): boolean {
    if (url.includes(TRACKING_ENDPOINT)) {
      const accessToken = localStorage.getItem('accessToken');
      const sendEncryptedData = (encryptedData: BodyInit | null): boolean => {
        if (!accessToken) {
          return originalSendBeacon.call(navigator, url, encryptedData);
        }
        sendTrackingRequest(url, encryptedData, accessToken);
        return true;
      };
      let encryptedData: BodyInit | null = null;
      
      if (data) {
        try {
          if (data instanceof Blob) {
            // 读取Blob数据
            const reader = new FileReader();
            reader.onload = function() {
              try {
                const jsonPayload = JSON.parse(reader.result as string);
                // 创建加密后的Blob
                encryptedData = createEncryptedBlob(jsonPayload);
                // 使用加密后的数据发送请求
                sendEncryptedData(encryptedData);
              } catch (e) {
                // 如果解析失败，仍然加密原始字符串
                encryptedData = createEncryptedBlob(reader.result);
                sendEncryptedData(encryptedData);
              }
            };
            reader.readAsText(data);
            return true; // 由于异步处理，这里返回true
          } else if (typeof data === 'string') {
            try {
              const jsonPayload = JSON.parse(data);
              // 加密JSON数据
              encryptedData = CryptoService.encrypt(jsonPayload);
            } catch (e) {
              // 如果解析失败，直接加密字符串
              encryptedData = CryptoService.encrypt(data);
            }
          } else {
            // 其他类型数据直接加密
            encryptedData = CryptoService.encrypt(data);
          }
        } catch (error) {
          console.error('payload error:', error);
          encryptedData = data; // 出错时使用原始数据
        }
      }
      
      return sendEncryptedData(encryptedData);
    }
    
    return originalSendBeacon.call(navigator, url, data);
  };
}

/**
 * ARMS SDK 在 Beacon 不可用或数据过大时会降级到 XMLHttpRequest。
 */
function initXMLHttpRequestInterceptor(): void {
  const originalOpen = XMLHttpRequest.prototype.open;
  const originalSend = XMLHttpRequest.prototype.send;

  const openWithTracking = function(
    this: TrackingXMLHttpRequest,
    method: string,
    url: string | URL,
    async = true,
    username?: string | null,
    password?: string | null
  ): void {
    this.isTrackingRequest = url.toString().includes(TRACKING_ENDPOINT);
    if (username !== undefined) {
      originalOpen.call(this, method, url, async, username, password ?? null);
      return;
    }
    originalOpen.call(this, method, url, async);
  };

  XMLHttpRequest.prototype.open = openWithTracking as XMLHttpRequest['open'];
  XMLHttpRequest.prototype.send = function(
    this: TrackingXMLHttpRequest,
    body?: Document | XMLHttpRequestBodyInit | null
  ): void {
    const accessToken = localStorage.getItem('accessToken');
    if (this.isTrackingRequest && accessToken) {
      this.setRequestHeader('Authorization', `Bearer ${accessToken}`);
    }
    originalSend.call(this, body);
  };
}

// write by cursor
/**
 * 初始化全局请求拦截器
 */
export function initializeRequestInterceptor() {
  // 初始化 SendBeacon 拦截器
  initSendBeaconInterceptor();
  initXMLHttpRequestInterceptor();
}
